import { FC, useId, useState } from "react";

// eslint-disable-next-line no-restricted-imports -- The dashboard design system has menu items but no menu container.
import Menu from "@mui/material/Menu";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem";
import { ChevronDown } from "@/icons/outlined";

interface CancelRenewalMenuProps {
  isLoading: boolean;
  onScheduleEndDate: () => void;
  onCancelImmediately: () => void;
}

export const CancelRenewalMenu: FC<CancelRenewalMenuProps> = ({
  isLoading,
  onScheduleEndDate,
  onCancelImmediately,
}) => {
  const { t } = useTranslate();
  const menuId = useId();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const selectAndClose = (action: () => void) => () => {
    setMenuAnchor(null);
    action();
  };

  return (
    <>
      <DashboardButton
        variant="outlined"
        size="small"
        vibe="danger"
        isLoading={isLoading}
        endIcon={<ChevronDown />}
        aria-controls={menuAnchor ? menuId : undefined}
        aria-expanded={Boolean(menuAnchor)}
        aria-haspopup="menu"
        data-test="cancel-renewal-menu-button"
        onClick={(event) => setMenuAnchor(event.currentTarget)}
      >
        {t("dashboard", "donorTransactionDetailDrawer.cancelRenewal")}
      </DashboardButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ id: menuId }}
      >
        <DashboardMenuItem
          size="medium"
          data-test="schedule-end-date-menu-item"
          onClick={selectAndClose(onScheduleEndDate)}
        >
          {t("dashboard", "donorTransactionDetailDrawer.scheduleEndDate")}
        </DashboardMenuItem>
        <DashboardMenuItem
          size="medium"
          color="error"
          sx={(theme) => ({ "&&": { color: theme.palette.text.danger.intense } })}
          data-test="cancel-immediately-menu-item"
          onClick={selectAndClose(onCancelImmediately)}
        >
          {t("dashboard", "donorTransactionDetailDrawer.cancelImmediately")}
        </DashboardMenuItem>
      </Menu>
    </>
  );
};
